/**
 * Channel Configuration Management
 * 
 * Utilities for managing user channel configurations in localStorage.
 * This will be replaced with DynamoDB API calls in production.
 */

const CHANNEL_CONFIG_KEY = 'snorkle_channel_config';

export const getDefaultChannelConfig = (userInfo) => ({
  channelName: userInfo?.name || 'My Channel',
  description: 'Welcome to my channel',
  avatar: '📱',
  missionStatement: '',
  institutionProfile: '',
  suggestionPreferences: {
    useMissionStatement: true,
    useInstitutionProfile: true,
    useContentAnalytics: true,
    useSpecificPrompt: false,
    specificPrompt: '',
    educationalLevel: 'general',
    numSlides: 5
  },
  selectedVisuals: [],
  visualOrder: [],
  settings: {
    privacy: 'public',
    slug: userInfo?.email?.split('@')[0] || 'user'
  },
  mockEngagementData: {},
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

export const loadChannelConfig = (userId) => {
  try {
    const key = `${CHANNEL_CONFIG_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error loading channel config:', error);
    return null;
  }
};

export const saveChannelConfig = (userId, config) => {
  try {
    const key = `${CHANNEL_CONFIG_KEY}_${userId}`;
    const updatedConfig = {
      ...config,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(updatedConfig));
    return updatedConfig;
  } catch (error) {
    console.error('Error saving channel config:', error);
    return null;
  }
};

export const addVisualToChannel = (userId, visualId, currentConfig) => {
  const config = currentConfig || loadChannelConfig(userId);
  if (!config) return null;

  if (!config.selectedVisuals.includes(visualId)) {
    config.selectedVisuals.push(visualId);
    config.visualOrder.push(visualId);
  }

  return saveChannelConfig(userId, config);
};

export const removeVisualFromChannel = (userId, visualId, currentConfig) => {
  const config = currentConfig || loadChannelConfig(userId);
  if (!config) return null;

  config.selectedVisuals = config.selectedVisuals.filter(id => id !== visualId);
  config.visualOrder = config.visualOrder.filter(id => id !== visualId);

  return saveChannelConfig(userId, config);
};

export const toggleVisualInChannel = (userId, visualId, currentConfig) => {
  const config = currentConfig || loadChannelConfig(userId);
  if (!config) return null;

  if (config.selectedVisuals.includes(visualId)) {
    return removeVisualFromChannel(userId, visualId, config);
  } else {
    return addVisualToChannel(userId, visualId, config);
  }
};

export const reorderChannelContent = (userId, newOrder, currentConfig) => {
  const config = currentConfig || loadChannelConfig(userId);
  if (!config) return null;

  config.visualOrder = newOrder;
  return saveChannelConfig(userId, config);
};

export const updateChannelSettings = (userId, updates, currentConfig) => {
  const config = currentConfig || loadChannelConfig(userId);
  if (!config) return null;

  const updatedConfig = {
    ...config,
    ...updates
  };

  return saveChannelConfig(userId, updatedConfig);
};

export const isVisualInChannel = (visualId, config) => {
  if (!config) return false;
  return config.selectedVisuals.includes(visualId);
};

export const getChannelVisualCount = (config) => {
  if (!config) return 0;
  return config.selectedVisuals.length;
};

export const clearChannelConfig = (userId) => {
  try {
    const key = `${CHANNEL_CONFIG_KEY}_${userId}`;
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error clearing channel config:', error);
    return false;
  }
};
